import {useContext, useEffect, useRef, useState} from 'react';
import {message} from 'antd';
import api from '../api';
import useCurrentOrg from './useCurrentOrg';
import {SessionContext} from '../../contexts/SessionContext';

const errorText = error => error?.response?.data?.error || error?.message || 'Google Drive is unavailable. Try again.';

export function useConnectGoogleDrive(onConnected) {
    const org = useCurrentOrg();
    const {dispatch} = useContext(SessionContext);
    const latest = useRef();
    latest.current = {org, onConnected};
    const cleanup = useRef(() => {});
    useEffect(() => () => cleanup.current(), [org?.id]);

    return async () => {
        cleanup.current();
        const orgId = org?.id;
        const flowId = window.crypto.randomUUID();
        // Open synchronously while the click still grants popup permission.
        const popup = window.open('about:blank', `google-drive-${flowId}`, 'popup,width=650,height=750');
        if (!popup) {
            message.error('Allow popups to connect Google Drive.');
            return;
        }
        let active = true;
        const connectUrl = new URL(window.Config.googleDriveConnectUrl || '/api/integrations/connect_google_drive', window.location.origin);
        const callbackOrigin = connectUrl.origin;
        const stop = () => {
            active = false;
            window.removeEventListener('message', receive);
            window.clearInterval(closedTimer);
            window.clearTimeout(timeout);
        };
        const receive = event => {
            if (!active || latest.current.org?.id !== orgId || event.origin !== callbackOrigin ||
                event.source !== popup || event.data?.type !== 'mediagraph:google-drive' || event.data.flow_id !== flowId) return;
            stop();
            popup.close();
            if (!event.data.success) {
                message.error(event.data.error || 'Google Drive access was not granted.');
                return;
            }
            dispatch({type: 'org_updated', org: {...latest.current.org,
                google_drive_connected: true, google_drive_account: event.data.account}});
            latest.current.onConnected?.();
        };
        window.addEventListener('message', receive);
        const closedTimer = window.setInterval(() => {
            if (popup.closed) {
                stop();
                if (latest.current.org?.id === orgId) message.info('Google Drive connection canceled.');
            }
        }, 500);
        const timeout = window.setTimeout(() => {
            stop();
            popup.close();
            message.error('Google Drive connection timed out. Try again.');
        }, 10 * 60 * 1000);
        cleanup.current = () => { stop(); popup.close(); };
        try {
            const jwt = JSON.parse(localStorage.getItem('app-reducer'))?.jwt;
            if (!jwt) throw new Error('Sign in again to connect Google Drive.');
            // A form keeps the JWT out of URLs and establishes the browser
            // binding cookie on the callback host before visiting Google.
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = connectUrl.href;
            form.target = `google-drive-${flowId}`;
            form.hidden = true;
            for (const [name, value] of Object.entries({jwt, org_id: orgId, flow_id: flowId})) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                input.value = value;
                form.appendChild(input);
            }
            document.body.appendChild(form);
            try { form.submit(); } finally { form.remove(); }
        } catch (error) {
            if (!active) return;
            stop();
            popup.close();
            message.error(errorText(error));
        }
    };
}

let pickerApi;
export function loadGooglePicker() {
    if (window.google?.picker) return Promise.resolve();
    if (!pickerApi) {
        pickerApi = new Promise((resolve, reject) => {
            const timeout = window.setTimeout(() => reject(new Error('Google Drive Picker could not load. Try again.')), 15000);
            const load = () => window.gapi.load('picker', {
                callback: () => { window.clearTimeout(timeout); resolve(); },
                onerror: () => { window.clearTimeout(timeout); reject(new Error('Google Drive Picker could not load.')); },
                timeout: 15000,
                ontimeout: () => reject(new Error('Google Drive Picker could not load. Try again.')),
            });
            if (window.gapi) return load();
            let script = document.querySelector('script[src="https://apis.google.com/js/api.js"]');
            if (!script) {
                script = document.createElement('script');
                script.src = 'https://apis.google.com/js/api.js';
                script.async = true;
                document.head.appendChild(script);
            }
            script.addEventListener('load', load, {once: true});
            script.addEventListener('error', () => {
                window.clearTimeout(timeout);
                script.remove();
                reject(new Error('Google Drive Picker could not load. Try again.'));
            }, {once: true});
        }).catch(error => { pickerApi = null; throw error; });
    }
    return pickerApi;
}

export function useGoogleDrivePicker({foldersOnly = false, onPick}) {
    const org = useCurrentOrg();
    const latest = useRef();
    latest.current = {orgId: org?.id, onPick};
    const sequence = useRef(0);
    const picker = useRef();
    const [loading, setLoading] = useState(false);
    const cancel = () => {
        sequence.current++;
        picker.current?.setVisible(false);
        picker.current = null;
        setLoading(false);
    };
    useEffect(() => {
        setLoading(false);
        return () => {
            sequence.current++;
            picker.current?.setVisible(false);
            picker.current = null;
        };
    }, [org?.id]);

    const open = async () => {
        cancel();
        const attempt = sequence.current;
        const orgId = org?.id;
        const handlePick = latest.current.onPick;
        const current = () => attempt === sequence.current && latest.current.orgId === orgId;
        setLoading(true);
        try {
            // Tokens are short-lived; obtain a fresh one on every opening.
            const [response] = await Promise.all([api.post('/api/integrations/refresh_google_drive', {}, {headers: {OrganizationId: orgId}}), loadGooglePicker()]);
            if (!current()) return;
            if (!response.data.access_token) throw new Error(response.data.error || 'Reconnect Google Drive to continue.');
            const view = new window.google.picker.DocsView()
                .setIncludeFolders(true).setSelectFolderEnabled(true);
            if (foldersOnly) view.setMimeTypes('application/vnd.google-apps.folder');
            else view.setMode(window.google.picker.DocsViewMode.LIST);
            const builder = new window.google.picker.PickerBuilder()
                .setAppId(window.Config.googleDriveAppId)
                .setDeveloperKey(window.Config.googleDriveDeveloperKey)
                .setOAuthToken(response.data.access_token)
                .setOrigin(window.location.origin)
                .addView(view)
                .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
                .setCallback(async result => {
                    if (!current()) return;
                    if (result.action === window.google.picker.Action.CANCEL) { cancel(); return; }
                    if (result.action !== window.google.picker.Action.PICKED) return;
                    // Consume this selection once, even if Picker repeats its callback.
                    sequence.current++;
                    picker.current = null;
                    setLoading(false);
                    if (!result.docs?.length) return;
                    try { await handlePick(result.docs, orgId); }
                    catch (error) { if (latest.current.orgId === orgId) message.error(errorText(error)); }
                });
            if (!foldersOnly) builder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
            picker.current = builder.build();
            picker.current.setVisible(true);
        } catch (error) {
            if (current()) message.error(errorText(error));
        } finally {
            if (current()) setLoading(false);
        }
    };
    return {open, cancel, loading};
}
